import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from 'react'

import type { ImageCropState } from '@/utilities/imageCropPlacement'

export type ImageCornerCoordinates = {
  topLeftLat: number
  topLeftLng: number
  topRightLat: number
  topRightLng: number
  bottomRightLat: number
  bottomRightLng: number
  bottomLeftLat: number
  bottomLeftLng: number
}

export type EditCornersTarget = {
  sourceId: string
  coordinates: ImageCornerCoordinates
  defaultCoordinates: ImageCornerCoordinates
  layerID: string
  layerName: string
  fileName: string
  label: string
  description: string
  minzoom: number
  /** Set when the layer source points at a `_crop` sibling (MAM-848). */
  imageCrop?: ImageCropState | null
}

export type EditCornersInteractionHandlers = {
  cancel: () => void
  switchTo: (target: EditCornersTarget) => void
  /**
   * Give the overlay first refusal on a map click (MAM-852 georeference
   * control points). Returns true when the click was consumed.
   */
  handleGeoreferenceMapClick?: (lngLat: { lng: number; lat: number }) => boolean
}

/** Which tool mode opens when the edit-corners overlay mounts. */
export type EditCornersInitialTool = 'neutral' | 'transform'

export type StartEditCornersOptions = {
  /**
   * Upload placement opens Transform (Save & next); double-click re-edit
   * opens the neutral tool-selection panel (MAM-851).
   */
  initialTool?: EditCornersInitialTool
}

interface ImportModalContextValue {
  isOpen: boolean
  pendingFiles: File[]
  /** Head of the edit-corners queue — the image currently being placed. */
  editCornersTarget: EditCornersTarget | null
  /** 1-based position of the current image within the queue (for "X of Y"). */
  editCornersIndex: number
  /** Total number of images queued for placement. */
  editCornersTotal: number
  /** Tool mode the active overlay should open in (persists across the queue). */
  editCornersInitialTool: EditCornersInitialTool
  /** Imperative bridge owned by the active edit-corners overlay. */
  editCornersInteractionRef: MutableRefObject<EditCornersInteractionHandlers | null>
  isEditCornersActive: boolean
  open: (opts?: { initialFiles?: File[] }) => void
  close: () => void
  consumePendingFiles: () => File[]
  /** Seed the placement queue and enter edit-corners for the first image. */
  startEditCorners: (
    targets: EditCornersTarget[],
    options?: StartEditCornersOptions
  ) => void
  /** Request cancel through the active edit-corners overlay. */
  cancelEditCorners: () => void
  /** Request a switch from the active image to another clicked image. */
  requestEditCornersSwitch: (target: EditCornersTarget) => void
  /** Replace the active edit-corners target with the requested image. */
  switchEditCornersTarget: (target: EditCornersTarget) => void
  /** Finish the current image and advance to the next (or exit when empty). */
  advanceEditCorners: () => void
}

const ImportModalContext = createContext<ImportModalContextValue | undefined>(
  undefined
)

export const ImportModalProvider = ({ children }: { children: ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [editCornersQueue, setEditCornersQueue] = useState<EditCornersTarget[]>(
    []
  )
  const [editCornersTotal, setEditCornersTotal] = useState(0)
  const [editCornersInitialTool, setEditCornersInitialTool] =
    useState<EditCornersInitialTool>('neutral')
  const editCornersInteractionRef =
    useRef<EditCornersInteractionHandlers | null>(null)

  const open = useCallback((opts?: { initialFiles?: File[] }) => {
    if (opts?.initialFiles?.length) setPendingFiles(opts.initialFiles)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => {
    setIsOpen(false)
    setPendingFiles([])
  }, [])

  const consumePendingFiles = useCallback(() => {
    const files = pendingFiles
    setPendingFiles([])
    return files
  }, [pendingFiles])

  const startEditCorners = useCallback(
    (targets: EditCornersTarget[], options?: StartEditCornersOptions) => {
      editCornersInteractionRef.current = null
      setEditCornersQueue(targets)
      setEditCornersTotal(targets.length)
      setEditCornersInitialTool(options?.initialTool ?? 'neutral')
    },
    []
  )

  const switchEditCornersTarget = useCallback((target: EditCornersTarget) => {
    editCornersInteractionRef.current = null
    setEditCornersQueue([target])
    setEditCornersTotal(1)
    setEditCornersInitialTool('neutral')
  }, [])

  const advanceEditCorners = useCallback(() => {
    editCornersInteractionRef.current = null
    setEditCornersQueue(prev => prev.slice(1))
  }, [])

  const cancelEditCorners = useCallback(() => {
    const handlers = editCornersInteractionRef.current
    if (handlers) {
      handlers.cancel()
      return
    }
    advanceEditCorners()
  }, [advanceEditCorners])

  const requestEditCornersSwitch = useCallback(
    (target: EditCornersTarget) => {
      const handlers = editCornersInteractionRef.current
      if (handlers) {
        handlers.switchTo(target)
        return
      }
      switchEditCornersTarget(target)
    },
    [switchEditCornersTarget]
  )

  const editCornersTarget = editCornersQueue[0] ?? null
  const editCornersIndex = editCornersTotal - editCornersQueue.length + 1

  const value = useMemo(
    () => ({
      isOpen,
      pendingFiles,
      editCornersTarget,
      editCornersIndex,
      editCornersTotal,
      editCornersInitialTool,
      editCornersInteractionRef,
      isEditCornersActive: editCornersQueue.length > 0,
      open,
      close,
      consumePendingFiles,
      startEditCorners,
      cancelEditCorners,
      requestEditCornersSwitch,
      switchEditCornersTarget,
      advanceEditCorners,
    }),
    [
      isOpen,
      pendingFiles,
      editCornersQueue,
      editCornersTarget,
      editCornersIndex,
      editCornersTotal,
      editCornersInitialTool,
      open,
      close,
      consumePendingFiles,
      startEditCorners,
      cancelEditCorners,
      requestEditCornersSwitch,
      switchEditCornersTarget,
      advanceEditCorners,
    ]
  )

  return (
    <ImportModalContext.Provider value={value}>
      {children}
    </ImportModalContext.Provider>
  )
}

export const useImportModal = (): ImportModalContextValue => {
  const context = useContext(ImportModalContext)
  if (!context) {
    throw new Error('useImportModal must be used within ImportModalProvider')
  }
  return context
}
